/**
 * Global event bus for coordinating UI state across components for Nuxt 2
 *
 * GLOBAL EVENT USAGE GUIDELINES:
 * 1. Always clean up in beforeDestroy/destroyed hooks
 * 2. Use bound functions, not anonymous functions
 * 3. Namespace event names: 'feature:action'
 * 4. Only for UI coordination, not data flow
 */

import { EventBus, EventBusHandler } from '../types';

export class GlobalEventBus {
  private events: Map<string, Set<EventBusHandler>> = new Map();

  on(event: string, callback: EventBusHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)!.add(callback);
  }

  off(event: string, callback: EventBusHandler): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.events.delete(event);
      }
    }
  }

  once(event: string, callback: EventBusHandler): void {
    const wrappedCallback: EventBusHandler = (...args: any[]) => {
      this.off(event, wrappedCallback);
      callback(...args);
    };
    this.on(event, wrappedCallback);
  }

  emit(event: string, ...args: any[]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in global event listener for "${event}":`, error);
        }
      });
    }
  }

  /**
   * Get the number of listeners for an event (useful for debugging)
   */
  listenerCount(event: string): number {
    return this.events.get(event)?.size || 0;
  }

  /**
   * Remove all listeners for a specific event
   */
  removeAllListeners(event?: string): void {
    if (event) {
      this.events.delete(event);
    } else {
      this.events.clear();
    }
  }
}

export default (_, inject) => {
  const eventBus: EventBus = new GlobalEventBus();
  inject('eventBus', eventBus);
};
